import React from "react";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import CoveredSummaryTableUI from "./CoveredSummaryTableUI";
import {
  getCoveredSummaryDetailTable,
  getCoveredSummaryExcelFile,
  getCoveredSummaryTable,
} from "src/Requests/imePortfolio";
import { FileDownloader } from "src/Components";
import type {
  ICoveredSummaryDetailFooter,
  ICoveredSummaryDetailSearchParams,
  ICoveredSummaryDetailTableReport,
  ICoveredSummarySearchParams,
  ICoveredSummaryTableReport,
} from "src/Interfaces/imePortfolio";
import { Header, Report } from "src/Interfaces/TableInterfaces";

// Columns that must NOT trigger a detail load when their cell is clicked.
const NON_DETAIL_HEADERS = ["Row", "Name"];

const CoveredSummary: React.FC = () => {
  const prevKeyRef = React.useRef<string | null>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
  // user name taken from the clicked master row's "name" column.
  const [selectedUserName, setSelectedUserName] = React.useState<string | null>(
    null,
  );
  // instrument extracted from the clicked header, used as a fallback ticker
  const [selectedInstrument, setSelectedInstrument] = React.useState<
    string | null
  >(null);
  const [detailSearchParams, setDetailSearchParams] =
    React.useState<ICoveredSummaryDetailSearchParams | null>(null);
  // userName / ticker come from the first footer item of the detail response.
  const [detailFooter, setDetailFooter] =
    React.useState<ICoveredSummaryDetailFooter | null>(null);

  const summaryCellClickHandler = (
    row: ICoveredSummaryTableReport,
    header: Header,
  ) => {
    // act based on the header: the # and name columns are not selectable.
    if (NON_DETAIL_HEADERS.includes(header.headerName)) return;

    const instrument = header?.groupName;
    if (!instrument) return;

    // value & coverage cells of the same instrument resolve to the same detail.
    const key = `${row.id}-${instrument}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;

    setSelectedRowId(row.id);
    setSelectedUserName(row.name ?? null);
    setSelectedInstrument(instrument);
    setDetailFooter(null);
    setDetailSearchParams({
      userId: row.id,
      instrumentId: instrument,
    });
  };

  const handleDetailResponse = (
    response: Report<ICoveredSummaryDetailTableReport>,
  ) => {
    const first = response.footer?.[0] as
      | (ICoveredSummaryDetailTableReport & ICoveredSummaryDetailFooter)
      | undefined;
    setDetailFooter(
      first ? { userName: first.userName, ticker: first.ticker } : null,
    );
  };

  return (
    <div className="flex h-full w-full flex-col gap-4 p-5">
      <FileDownloader getFile={getCoveredSummaryExcelFile} />

      <div className="w-fit">
        <TableTemplate<ICoveredSummarySearchParams, ICoveredSummaryTableReport>
          getDataTable={getCoveredSummaryTable}
          withSort
          TableUI={({ reports, onAction, sortingType, setSortingType }) => (
            <CoveredSummaryTableUI
              reports={reports}
              onAction={onAction}
              sortingType={sortingType}
              setSortingType={setSortingType}
              onCellClick={summaryCellClickHandler}
              isCellActive={(row, header) =>
                row.id === selectedRowId &&
                !!header.groupName &&
                header.groupName === selectedInstrument
              }
              tableBoxClassName="max-h-[400px] overflow-auto"
            />
          )}
          tableWrapperClassname="!mx-0"
        />
      </div>

      {detailSearchParams ? (
        <>
          <div className="flex min-h-8 items-center gap-6">
            <span className="text-sm font-bold">
              نام کاربر: {selectedUserName ?? "-"}
            </span>
            <span className="text-sm font-bold">
              نماد پایه: {detailFooter?.ticker ?? selectedInstrument ?? "-"}
            </span>
          </div>

          <TableTemplate<
            ICoveredSummaryDetailSearchParams,
            ICoveredSummaryDetailTableReport
          >
            key={`${detailSearchParams.userId}-${detailSearchParams.instrumentId}`}
            getDataTable={getCoveredSummaryDetailTable}
            searchParameters={detailSearchParams}
            onResponse={handleDetailResponse}
            withSort
            TableUI={({ reports, onAction, sortingType, setSortingType }) => (
              <CommonTableUI
                reports={reports}
                onAction={onAction}
                sortingType={sortingType}
                setSortingType={setSortingType}
                tableBoxClassName="max-h-[500px] overflow-auto"
              />
            )}
            tableWrapperClassname="!mx-0"
          />
        </>
      ) : null}
    </div>
  );
};

export default CoveredSummary;
